import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ServiceItem } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import {
  useServiceActivate,
  useServiceDeactivate,
  useServicesList,
  type ServiceListFilter,
} from './api';
import { ServiceFormModal } from './ServiceFormModal';

export function ServicesPage(): ReactNode {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<ServiceListFilter['active']>('true');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<ServiceItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const filter: ServiceListFilter = useMemo(
    () => ({
      active: activeFilter,
      search: search.trim() || undefined,
      category: category.trim() || undefined,
    }),
    [activeFilter, search, category],
  );

  const { data: services = [], isLoading, error } = useServicesList(filter);
  const deactivate = useServiceDeactivate();
  const activate = useServiceActivate();

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const s of services) if (s.category) set.add(s.category);
    return Array.from(set).sort();
  }, [services]);

  const priceFmt = useMemo(
    () =>
      new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
        minimumFractionDigits: 2,
      }),
    [],
  );

  const onCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const onEdit = (s: ServiceItem) => {
    setEditing(s);
    setFormOpen(true);
  };

  const onToggleActive = async (s: ServiceItem) => {
    setActionError(null);
    try {
      if (s.active) await deactivate.mutateAsync(s.id);
      else await activate.mutateAsync(s.id);
    } catch (err) {
      setActionError(extractApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{t('services.title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('services.subtitle')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={onCreate}>
          {t('services.actions.add')}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">{t('common.filter.status')}:</span>
          {(['true', 'false', 'all'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setActiveFilter(opt)}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                activeFilter === opt
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-zinc-600 hover:bg-zinc-100'
              }`}
            >
              {t(`common.filter.${opt === 'true' ? 'activeOnly' : opt === 'false' ? 'inactiveOnly' : 'all'}`)}
            </button>
          ))}
        </div>

        <div className="flex flex-1 flex-wrap items-center gap-3">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input max-w-[14rem]"
          >
            <option value="">{t('services.filter.allCategories')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <input
            type="search"
            placeholder={t('services.filter.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1 min-w-[12rem]"
          />
        </div>
      </div>

      {actionError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {actionError}
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {extractApiError(error)}
        </div>
      )}

      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200">
        <table className="min-w-full divide-y divide-zinc-200">
          <thead className="bg-zinc-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">{t('services.table.name')}</th>
              <th className="px-4 py-3">{t('services.table.category')}</th>
              <th className="px-4 py-3">{t('services.table.duration')}</th>
              <th className="px-4 py-3">{t('services.table.price')}</th>
              <th className="px-4 py-3">{t('services.table.status')}</th>
              <th className="px-4 py-3 text-right">{t('services.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  {t('common.loading')}
                </td>
              </tr>
            ) : services.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  {t('services.empty')}
                </td>
              </tr>
            ) : (
              services.map((s) => (
                <tr key={s.id} className={s.active ? '' : 'bg-zinc-50/60'}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{s.name}</div>
                    {s.description && (
                      <div className="mt-0.5 truncate text-xs text-zinc-500" title={s.description}>
                        {s.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{s.category ?? '—'}</td>
                  <td className="px-4 py-3 text-zinc-700">
                    {s.durationMinutes} {t('common.minutesShort')}
                  </td>
                  <td className="px-4 py-3 font-medium text-zinc-900">
                    {priceFmt.format(Number(s.price))}
                  </td>
                  <td className="px-4 py-3">
                    {s.active ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                        {t('common.status.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500 ring-1 ring-zinc-200">
                        {t('common.status.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        className="text-sm text-brand-600 hover:text-brand-700"
                        onClick={() => onEdit(s)}
                      >
                        {t('common.actions.edit')}
                      </button>
                      <button
                        type="button"
                        className="text-sm text-zinc-500 hover:text-zinc-800"
                        onClick={() => void onToggleActive(s)}
                      >
                        {s.active ? t('common.actions.deactivate') : t('common.actions.activate')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ServiceFormModal
        open={formOpen}
        service={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
